// App.js
import React, { useState, useEffect } from 'react';
import useAuth from './hooks/useAuth';
import useOrientation from './hooks/useOrientation';
import OrientationWarning from './components/OrientationWarning';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';
import UnauthenticatedLayout from './layouts/UnauthenticatedLayout';
import SWUpdatePrompt from './components/SWUpdatePrompt';
import useServiceWorkerUpdates from './hooks/useServiceWorkerUpdates';
import { initializeDB } from './config/db';
import { DB_VERSION } from './config/constants';
import jhaSyncService from './services/jhaSyncService';
import poEditService from './services/poEditService';
import versionService from './services/versionService';
import { syncAllData } from './services/syncService';
import unifiedFormService from './services/unifiedFormService';
import fallArrestRescuePlanService from './services/fallArrestRescuePlanService';
import SyncNotifications from './components/jha/SyncNotifications';
import SyncProgressNotification from './components/SyncProgressNotification';

function App() {
    const { isAuthenticated, handleLogout } = useAuth();
    const { isLandscape, isMobile } = useOrientation();
    const { waitingWorker, updateApp } = useServiceWorkerUpdates();

    const [isModalOpen, setModalOpen] = useState(false);

    // Если появилась новая версия, можно по желанию автоматически открыть модалку,
    // или открыть по клику:
    // Здесь решаем: открывать ли модалку сразу
    useEffect(() => {
        if (waitingWorker) {
            // Можете решить автоматом открывать модалку:
            //setModalOpen(true);
        }
    }, [waitingWorker]);

    // Check if app was updated and run sync
    useEffect(() => {
        // Check both flags for app update
        const appUpdated = localStorage.getItem('app_updated');
        const syncAfterUpdate = localStorage.getItem('sync_after_update');
        
        if ((appUpdated === 'true' || syncAfterUpdate === 'true') && isAuthenticated) {
            console.log('App was updated, running sync...');
            localStorage.removeItem('app_updated');
            localStorage.removeItem('sync_after_update');
            
            // Run sync after a short delay to ensure app is fully loaded
            setTimeout(() => {
                syncAllData().then(() => {
                    console.log('Sync completed after app update');
                    // Update last sync time - set both keys to be safe
                    const syncTime = new Date().toISOString();
                    localStorage.setItem('last_sync', syncTime); // old key
                    localStorage.setItem('lastOverallSyncTime', syncTime); // correct key used by components
                }).catch(err => {
                    console.error('Error syncing after update:', err);
                });
            }, 1000);
        }
    }, [isAuthenticated]);

    // Start JHA auto sync when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            jhaSyncService.startAutoSync(5); // Sync every 5 minutes
        }
    }, [isAuthenticated]);

    // Start PO edit auto sync when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            poEditService.startAutoSync(5); // Sync pending PO edits every 5 minutes
        }
    }, [isAuthenticated]);

    // Initialize version checking
    useEffect(() => {
        // Start version checking service; обновляться самому, пока есть связь.
        versionService.init().finally(() => versionService.startAutoUpdate());
        
        // Cleanup on unmount
        return () => {
            versionService.stop();
        };
    }, []);

    // Ensure IndexedDB upgrade runs after app update or DB_VERSION change
    useEffect(() => {
        // Force reopen DB when app was updated
        const appUpdated = localStorage.getItem('app_updated') === 'true';
        const syncAfterUpdate = localStorage.getItem('sync_after_update') === 'true';
        const storedDbVersion = parseInt(localStorage.getItem('db_version') || '0', 10);
        const dbVersionChanged = !Number.isNaN(DB_VERSION) && DB_VERSION > storedDbVersion;

        if (appUpdated || syncAfterUpdate || dbVersionChanged) {
            // Force DB reopen to trigger upgrade callback
            initializeDB(true).then(() => {
                localStorage.setItem('db_version', String(DB_VERSION));
            }).catch(() => {});
        }
    }, []);

    // Try syncing any pending unified forms when authenticated or back online
    // DISABLED: Auto-sync is turned off
    // useEffect(() => {
    //     if (!isAuthenticated) return;
    //     const sync = () => unifiedFormService.syncPending().catch(() => {});
    //     sync();
    //     window.addEventListener('online', sync);
    //     return () => window.removeEventListener('online', sync);
    // }, [isAuthenticated]);

    // Fall Arrest Rescue Plan: автосинк pending submissions.
    // Триггеры — приход на app, появление сети.
    useEffect(() => {
        if (!isAuthenticated) return;
        const sync = () => fallArrestRescuePlanService.syncPending().catch(() => {});
        // Initial sync (если уже online при mount)
        sync();
        window.addEventListener('online', sync);
        return () => window.removeEventListener('online', sync);
    }, [isAuthenticated]);

    if (!isLandscape && isMobile) {
        return <OrientationWarning />;
    }

    return (
        <>
            <SWUpdatePrompt
                open={isModalOpen}
                onClose={() => setModalOpen(false)}
                waitingWorker={waitingWorker}
                updateApp={updateApp}
            />
            {isAuthenticated ? (
                <AuthenticatedLayout handleLogout={handleLogout}
                                     onShowUpdateModal={() => setModalOpen(true)}
                                     waitingWorker={waitingWorker}/>
            ) : (
                <UnauthenticatedLayout />
            )}

            {/* Global Sync Notifications - works on all pages */}
            {isAuthenticated && <SyncNotifications />}
            {isAuthenticated && <SyncProgressNotification />}
        </>
    );
}

export default App;
